<template>
  <main class="content">
    <Hero :title="data?.header?.title || 'Registreren'"/>
    <form class="block maxw--neck form" @submit.prevent="submit">
      <article class="text--center" v-html="$md.render(data?.content || '')"></article>
      <div class="form__row">
        <FormTextField class="field--w1-2" name="firstname" label="Voornaam" v-model="v$.firstname.$model" :validator="v$.firstname"/>
        <FormTextField class="field--w1-2" name="lastname" label="Achternaam" v-model="v$.lastname.$model" :validator="v$.lastname"/>
      </div>
      <div class="form__row">
        <FormTextField name="email" type="email" label="Email" v-model="v$.email.$model" :validator="v$.email"/>
      </div>
      <div class="form__row">
        <FormTextField name="address" label="Adres" v-model="v$.address.$model" :validator="v$.address"/>
      </div>
      <div class="form__row" v-if="!isDisabled">
        <NuxtTurnstile v-model="token" class="mt-2 mx-auto"/>
      </div>
      <div class="form__row">
        <div class="field">
          <button class="btn btn--full mt-2" type="submit" :disabled="isLoading">
            Verzenden
            <Spinner v-if="isLoading" />
          </button>
        </div>
      </div>
      <div class="form__row" v-if="!neutral">
        <span class="mt-1 field__status text--center" :class="feedbackClass">{{ feedbackMessage }}</span>
      </div>
    </form>
  </main>
</template>

<script lang="ts" setup>
import useVuelidate from '@vuelidate/core';
import { email, helpers, required } from '@vuelidate/validators';
import type { default as Registration, RegistrationSubmission } from '@/typings/strapi/objects/registration';
import type RegisterPage from '~/typings/strapi/pages/registerPage';

const { findOne } = useStrapi(); 

const { data } = await useAsyncData(
  'register-page',
  () => findOne<RegisterPage>('register-page', { populate: ['header'] }),
  {
    transform: flattenObject
  }
)

useHead({
  title: 'Registreren'
});

const { create } = useStrapi();
const { token, isDisabled, verify } = useTurnstile();
const { isLoading, startLoading, stopLoading } = useLoading();
const { feedbackMessage, resetStatus, setStatus, handleSuccess, neutral, feedbackClass } = useSubmitFeedback(
  'Registratie verzonden',
  'Er is iets fout gegaan. Probeer het later opnieuw'
);

const baseFormData = {
  firstname: '',
  lastname: '',
  email: '',
  address: ''
}

const formData = reactive<RegistrationSubmission>({ ...baseFormData });

async function submit(e: Event) {
  resetStatus();

  if (!(await v$.value.$validate()) || isLoading.value) return;

  startLoading();

  try {
    if (!(await verify())) {
      // Verification with turnstile failed
      setStatus(false);
      return;
    }

    await create<Registration>('registrations', formData);

    handleSuccess();
  } catch (e) {
    setStatus(false);
  } finally {
    stopLoading();
  }
}

const rules = computed(() => ({
  firstname: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  lastname: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  email: {
    required: helpers.withMessage('Dit veld is verplicht.', required),
    email: helpers.withMessage('U dient een geldig emailadres in te vullen.', email)
  },
  address: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  }
}));

const v$ = useVuelidate(rules, formData);

definePageMeta({
  pageTransition: pageTransitionConfig,
});
</script>
